import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScriptScene } from '@/api';

interface Props {
  open: boolean;
  onClose: () => void;
  scenes: ScriptScene[];
  projectId: string;
  projectTitle: string;
  selectedStyle: 'biblical' | 'african' | 'modern';
  onGenerationStart: () => void;
  onGenerationComplete: (generatedScenes: ScriptScene[]) => void;
}

export default function ImageGenerationModal({
  open,
  onClose,
  scenes,
  projectId,
  projectTitle,
  selectedStyle,
  onGenerationStart,
  onGenerationComplete
}: Props) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [backgroundMode, setBackgroundMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentScene, setCurrentScene] = useState(0);
  const [error, setError] = useState('');

  const handleGenerateImages = async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    setError('');
    setProgress(0);
    setCurrentScene(0);
    onGenerationStart();

    try {
      // Simulate progress (real progress would come from server-sent events or polling)
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 500);

      const response = await fetch(`/api/projects/${projectId}/generate-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          style: selectedStyle,
          sceneIds: scenes.map(s => s.sceneNumber)
        })
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        throw new Error('Failed to generate images');
      }

      const result = await response.json();
      setProgress(100);

      // Simulate a brief completion view before continuing
      setTimeout(() => {
        onGenerationComplete(result.scenes || scenes);
        if (!backgroundMode) {
          onClose();
        }
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate images');
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            Generate Images for "{projectTitle}"
          </DialogTitle>
        </DialogHeader>

        {!isGenerating ? (
          <div className="space-y-6">
            {/* Style Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 mb-1">Visual Style</p>
                  <p className="text-lg font-semibold text-blue-600 capitalize">
                    {selectedStyle === 'biblical' && '📖 Biblical Renaissance'}
                    {selectedStyle === 'african' && '🌍 African Traditional'}
                    {selectedStyle === 'modern' && '✨ Modern Illustration'}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    {selectedStyle === 'biblical' && 'Classic oil painting style, dramatic lighting, religious art'}
                    {selectedStyle === 'african' && 'Vibrant colors, cultural patterns, warm earth tones'}
                    {selectedStyle === 'modern' && 'Digital art, cinematic lighting, vibrant colors'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">{scenes.length}</p>
                  <p className="text-xs text-gray-600">scenes to generate</p>
                </div>
              </div>
            </div>

            {/* Cost Estimate */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="text-xl">⚡</div>
                <div>
                  <p className="font-medium text-gray-900">Estimated Cost</p>
                  <p className="text-sm text-gray-600 mt-1">
                    ~${(scenes.length * 0.04).toFixed(2)} for {scenes.length} images at $0.04/image (DALL-E 3)
                  </p>
                </div>
              </div>
            </div>

            {/* Generation Options */}
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => setBackgroundMode(false)}>
                <input
                  type="radio"
                  checked={!backgroundMode}
                  onChange={() => setBackgroundMode(false)}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium text-gray-900">Stay and Watch (Recommended)</p>
                  <p className="text-sm text-gray-600">Watch the generation progress in real-time</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => setBackgroundMode(true)}>
                <input
                  type="radio"
                  checked={backgroundMode}
                  onChange={() => setBackgroundMode(true)}
                  className="w-4 h-4"
                />
                <div>
                  <p className="font-medium text-gray-900">Generate in Background</p>
                  <p className="text-sm text-gray-600">Continue to the editor while images generate</p>
                </div>
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateImages}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Generate Images
              </button>
            </div>
          </div>
        ) : (
          /* Loading State */
          <div className="space-y-8 py-8">
            {/* Animated Loading Graphic */}
            <div className="flex justify-center">
              <svg className="w-32 h-32" viewBox="0 0 200 200">
                {/* Animated image generation visualization */}
                <defs>
                  <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.3" />
                    <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.3" />
                    <animate attributeName="x1" from="-100%" to="100%" dur="2s" repeatCount="indefinite" />
                  </linearGradient>
                </defs>

                {/* Scene cards being generated */}
                {[0, 1, 2].map((i) => (
                  <g key={i}>
                    <rect
                      x={30 + i * 50}
                      y={50 + (i % 2) * 60}
                      width="40"
                      height="50"
                      fill={i < Math.ceil(progress / 33) ? '#06b6d4' : '#e5e7eb'}
                      opacity={i < Math.ceil(progress / 33) ? 1 : 0.3}
                      rx="4"
                    />
                    <circle
                      cx={50 + i * 50}
                      cy={75 + (i % 2) * 60}
                      r="8"
                      fill="none"
                      stroke={i < Math.ceil(progress / 33) ? '#0891b2' : '#d1d5db'}
                      strokeWidth="1.5"
                    />
                  </g>
                ))}

                {/* Outer rotating circle */}
                <circle
                  cx="100"
                  cy="100"
                  r="90"
                  fill="none"
                  stroke="url(#shimmer)"
                  strokeWidth="3"
                  strokeDasharray="565"
                  strokeDashoffset={565 * (1 - progress / 100)}
                  strokeLinecap="round"
                />
              </svg>
            </div>

            {/* Progress Information */}
            <div className="text-center space-y-4">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  Generating images...
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Scene {currentScene + 1} of {scenes.length}
                </p>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <p className="text-xs text-gray-600">{Math.round(progress)}% complete</p>

              {/* Scene Preview */}
              {currentScene < scenes.length && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs font-medium text-blue-900 uppercase tracking-wide mb-2">Current Scene</p>
                  <p className="font-medium text-gray-900">{scenes[currentScene].title}</p>
                  <p className="text-sm text-gray-600 mt-1">{scenes[currentScene].visualDescription}</p>
                </div>
              )}
            </div>

            {/* Completion Message */}
            {progress >= 100 && (
              <div className="text-center space-y-3">
                <div className="text-4xl">✨</div>
                <p className="font-semibold text-gray-900">Images generated successfully!</p>
                <p className="text-sm text-gray-600">
                  {backgroundMode ? 'You can now continue to the editor.' : 'Opening editor...'}
                </p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
